/**
 * Single source of truth for pricing model generation
 * Used across BusinessSnapshot, Pricing & Value Ladder, and descriptions
 */

export type PricingTier = {
  name: string;
  price: string;
  shortTagline: string;
  longDescription: string;
};

export type PricingModel = {
  summary: string;
  tiers: PricingTier[];
  model: string;
};

export type IdeaData = {
  revenue_potential_score?: number | null;
  opportunity_score?: number | null;
  feasibility_score?: number | null;
  market_size_usd?: number | null;
  estimated_arr_usd?: number | null;
  niche?: string | null;
  problem?: string | null;
  solution?: string | null;
  target_customer?: string | null;
  icp?: string | null;
  pricing_model?: string | null;
  offer?: any; // Legacy field to be migrated
};

/**
 * Generates a dynamic pricing model tailored to the startup idea
 * @param idea - The startup idea data
 * @returns A pricing model with tiers, summary, and model type
 */
export const generatePricingModel = (idea: IdeaData): PricingModel => {
  // Extract key metrics
  const revScore = idea.revenue_potential_score || 50;
  const oppScore = idea.opportunity_score || 50;
  const feasScore = idea.feasibility_score || 50;
  const marketSize = idea.market_size_usd || 100000000;
  const estimatedArr = idea.estimated_arr_usd || 500000;
  
  const category = idea.niche || 'SaaS';
  const problemText = idea.problem || 'solving a key market problem';
  const solutionText = idea.solution || 'providing an innovative solution';
  const targetCustomer = idea.target_customer || idea.icp || 'customers';
  
  // Determine market type (B2B vs B2C)
  const isB2B = idea.icp?.toLowerCase().includes('business') || 
                idea.icp?.toLowerCase().includes('enterprise') ||
                idea.icp?.toLowerCase().includes('company') ||
                idea.icp?.toLowerCase().includes('team') ||
                idea.target_customer?.toLowerCase().includes('business') ||
                idea.target_customer?.toLowerCase().includes('enterprise') ||
                category.toLowerCase().includes('b2b');
  
  const isB2C = !isB2B;
  
  // Determine market characteristics
  const isNicheMarket = oppScore < 60 || marketSize < 500000000;
  const isHighValue = revScore > 70 || estimatedArr > 1000000;
  const isLowComplexity = feasScore > 70;
  
  // Calculate base pricing multipliers
  // Revenue potential: 0.5x - 2.0x
  const revMultiplier = Math.max(0.5, Math.min(2.0, revScore / 50));
  
  // Market size: 0.7x - 1.5x
  const marketMultiplier = marketSize > 5000000000 ? 1.5 :
                         marketSize > 1000000000 ? 1.2 :
                         marketSize > 100000000 ? 1.0 :
                         0.7;
  
  // Opportunity score: 0.8x - 1.3x
  const opportunityMultiplier = Math.max(0.8, Math.min(1.3, oppScore / 60));
  
  // Combined multiplier
  const overallMultiplier = revMultiplier * marketMultiplier * opportunityMultiplier;
  
  // Calculate pricing tiers dynamically
  let starterBasePrice: number;
  let proBasePrice: number;
  let enterprisePrice: string;
  
  if (isB2B) {
    // B2B base prices scale with market
    starterBasePrice = isNicheMarket ? 25 : 40;
    proBasePrice = isNicheMarket ? 80 : 150;
    enterprisePrice = 'Custom';
  } else {
    // B2C base prices are lower
    starterBasePrice = isNicheMarket ? 4 : 8;
    proBasePrice = isNicheMarket ? 12 : 25;
    
    // B2C enterprise is a fixed premium tier
    const enterpriseBase = isNicheMarket ? 40 : 80;
    const enterpriseCalculated = Math.round(enterpriseBase * overallMultiplier);
    enterprisePrice = `$${enterpriseCalculated}/mo`;
  }
  
  // Apply multipliers to calculate final prices
  const starterPrice = Math.round(starterBasePrice * overallMultiplier);
  const proPrice = Math.round(proBasePrice * overallMultiplier);
  
  // Apply sensible bounds
  const boundedStarterPrice = isB2C 
    ? Math.max(3, Math.min(20, starterPrice))
    : Math.max(15, Math.min(100, starterPrice));
  
  const boundedProPrice = isB2C
    ? Math.max(8, Math.min(99, proPrice))
    : Math.max(25, Math.min(999, proPrice));
  
  // Build tier descriptions based on the specific idea
  const productType = category.includes('SaaS') ? 'platform' : 
                     category.includes('marketplace') ? 'marketplace' : 
                     category.includes('app') ? 'app' : 'product';
  
  const freeTierName = isB2B ? 'Starter' : 'Free';
  const freeTierPrice = isB2B ? `$${boundedStarterPrice}/mo` : '$0';
  
  // Extract key features from problem/solution
  const extractKeyPhrase = (text: string, maxWords: number = 4): string => {
    const words = text.toLowerCase().replace(/[^\w\s]/g, '').split(/\s+/).filter(w => w.length > 3);
    return words.slice(0, maxWords).join(' ');
  };
  
  const coreProblemPhrase = extractKeyPhrase(problemText, 3);
  const solutionPhrase = extractKeyPhrase(solutionText, 3);
  
  // Free/Starter tier
  const freeTierShortTagline = isB2B 
    ? `Essential tools for ${targetCustomer.toLowerCase()} getting started`
    : `Core ${productType} features to start addressing ${coreProblemPhrase}`;
  
  const freeTierLongDescription = isB2B
    ? `${freeTierName} plan (${freeTierPrice}) provides the foundational ${productType} capabilities for small teams or individual ${targetCustomer.toLowerCase()}. Includes basic access to address ${coreProblemPhrase} with standard support and core integrations.`
    : `${freeTierName} tier gives you immediate access to the core ${productType} features. Perfect for individuals and hobbyists who want to explore ${solutionPhrase} without commitment. Limited to basic usage with community support.`;
  
  // Pro tier
  const proTierShortTagline = isB2B
    ? `Full ${productType} power with advanced ${solutionPhrase} capabilities`
    : `Unlock premium features and scale your ${coreProblemPhrase} solution`;
  
  const proTierLongDescription = isB2B
    ? `Pro plan ($${boundedProPrice}/mo) unlocks the complete ${productType} with advanced analytics, priority support, and unlimited seats for growing teams. Ideal for ${targetCustomer.toLowerCase()} who need ${solutionPhrase} at scale with seamless integrations and dedicated onboarding.`
    : `Pro tier ($${boundedProPrice}/mo) removes all limits and gives you access to premium features including ${solutionPhrase}, advanced customization, priority support, and enhanced analytics. Perfect for power users and small teams who are serious about ${coreProblemPhrase}.`;
  
  // Enterprise/Premium tier
  const enterpriseTierName = isB2B ? 'Enterprise' : 'Premium';
  const enterpriseTierShortTagline = isB2B
    ? `Custom solutions with dedicated support for large ${targetCustomer.toLowerCase()}`
    : `White-label access, API, and unlimited scale for ${coreProblemPhrase}`;
  
  const enterpriseTierLongDescription = isB2B
    ? `${enterpriseTierName} plan (${enterprisePrice}) is tailored for large organizations requiring custom ${productType} deployments, dedicated account management, SLA guarantees, and volume pricing. Includes on-premise options, SSO, compliance support, and direct engineering access for mission-critical ${solutionPhrase} needs.`
    : `${enterpriseTierName} tier (${enterprisePrice}) provides white-label capabilities, full API access, unlimited usage, and custom integrations. Designed for agencies, resellers, or high-volume users who want to embed ${solutionPhrase} into their own products or services.`;
  
  const model = idea.pricing_model || (isB2B ? 'Subscription SaaS' : 'Freemium');
  
  const summaryText = isB2B
    ? `${model} pricing optimized for ${targetCustomer.toLowerCase()}. Starting at ${freeTierPrice} for small teams, scaling to $${boundedProPrice}/mo for full access, with custom enterprise solutions for large deployments.`
    : `${model} strategy designed to acquire users through a ${freeTierName.toLowerCase()} tier, convert them to Pro at $${boundedProPrice}/mo for premium features, and offer ${enterpriseTierName} at ${enterprisePrice} for power users and businesses.`;
  
  return {
    summary: summaryText,
    tiers: [
      {
        name: freeTierName,
        price: freeTierPrice,
        shortTagline: freeTierShortTagline,
        longDescription: freeTierLongDescription
      },
      {
        name: 'Pro',
        price: `$${boundedProPrice}/mo`,
        shortTagline: proTierShortTagline,
        longDescription: proTierLongDescription
      },
      {
        name: enterpriseTierName,
        price: enterprisePrice,
        shortTagline: enterpriseTierShortTagline,
        longDescription: enterpriseTierLongDescription
      }
    ],
    model
  };
};